import { Canvas as CanvasType, FabricObject } from 'fabric';
import { getObjectName } from './fabricHelpers';

interface ResizeOptions {
  canvas: CanvasType;
  oldWidth: number;
  oldHeight: number;
  newWidth: number;
  newHeight: number;
  isMobile: boolean;
}

export const handleCanvasResize = ({
  canvas,
  oldWidth,
  oldHeight,
  newWidth,
  newHeight,
  isMobile
}: ResizeOptions) => {
  if (!canvas || oldWidth === 0 || oldHeight === 0) return;

  // Calculate scale factors
  const scaleX = newWidth / oldWidth;
  const scaleY = newHeight / oldHeight;
  const scaleFactor = Math.min(scaleX, scaleY); // Use minimum to maintain aspect ratio

  // Get all objects on canvas
  const objects = canvas.getObjects();
  
  // Group objects by type for different handling
  const questionElements: FabricObject[] = [];
  const opticalChoices: FabricObject[] = [];
  const drawings: FabricObject[] = [];
  const others: FabricObject[] = [];

  objects.forEach(obj => {
    const name = getObjectName(obj);
    
    if (name === 'questionBackground' || name === 'parentQuestionBackground' ||
        name === 'questionFrame' || name === 'questionResizeHandle' || 
        name === 'questionMoveHandle' || name === 'questionMoveIcon') {
      questionElements.push(obj as FabricObject);
    } else if (name?.startsWith('opticalChoice_') || 
               name?.startsWith('opticalChoiceFeedback_') ||
               name?.startsWith('opticalQuestionNumber_')) {
      opticalChoices.push(obj as FabricObject);
    } else if (obj.type === 'path' || obj.type === 'line' || 
               obj.type === 'circle' || obj.type === 'rect' ||
               obj.type === 'ellipse' || obj.type === 'polygon') {
      drawings.push(obj as FabricObject);
    } else {
      others.push(obj as FabricObject);
    }
  });

  // Handle question elements specially
  let questionScale = 1;
  let questionOffsetX = 0;
  let questionOffsetY = 0;
  
  const questionBg = questionElements.find(obj => getObjectName(obj) === 'questionBackground');
  if (questionBg) {
    const imgWidth = questionBg.width || 1;
    const imgHeight = questionBg.height || 1;
    const currentScale = questionBg.scaleX || 1;
    const currentLeft = questionBg.left || 0;
    const currentTop = questionBg.top || 0;
    
    // Calculate new scale for question
    questionScale = currentScale * scaleFactor;
    
    // Apply canvas-specific limits
    const margin = isMobile ? 5 : 20;
    const maxAllowedWidth = newWidth - (margin * 2);
    const maxAllowedHeight = newHeight - (margin * 2);
    
    if (imgWidth * questionScale > maxAllowedWidth) {
      questionScale = maxAllowedWidth / imgWidth;
    }
    if (imgHeight * questionScale > maxAllowedHeight) {
      questionScale = Math.min(questionScale, maxAllowedHeight / imgHeight);
    }
    
    questionScale = Math.max(0.3, questionScale); // Minimum scale
    
    // Calculate new position
    const newLeft = currentLeft * scaleX;
    const newTop = currentTop * scaleY;
    
    // Store offset for relative positioning
    questionOffsetX = newLeft - currentLeft;
    questionOffsetY = newTop - currentTop;
    
    // Update question background
    questionBg.set({
      left: newLeft,
      top: newTop,
      scaleX: questionScale,
      scaleY: questionScale
    });
    questionBg.setCoords();
  }

  // Scale and reposition drawings that are on the question
  drawings.forEach(drawing => {
    const currentLeft = drawing.left || 0;
    const currentTop = drawing.top || 0;
    const currentScaleX = drawing.scaleX || 1;
    const currentScaleY = drawing.scaleY || 1;
    
    // Check if drawing is within question bounds (approximately)
    if (questionBg) {
      const qLeft = questionBg.left || 0;
      const qTop = questionBg.top || 0;
      const qWidth = (questionBg.width || 0) * (questionBg.scaleX || 1);
      const qHeight = (questionBg.height || 0) * (questionBg.scaleY || 1);
      
      const isOnQuestion = currentLeft >= qLeft - 50 && 
                          currentLeft <= qLeft + qWidth + 50 &&
                          currentTop >= qTop - 50 && 
                          currentTop <= qTop + qHeight + 50;
      
      if (isOnQuestion) {
        // Scale with question
        drawing.set({
          left: currentLeft + questionOffsetX,
          top: currentTop + questionOffsetY,
          scaleX: currentScaleX * (questionScale / (questionBg.scaleX || 1)),
          scaleY: currentScaleY * (questionScale / (questionBg.scaleY || 1))
        });
      } else {
        // Scale with canvas
        drawing.set({
          left: currentLeft * scaleX,
          top: currentTop * scaleY,
          scaleX: currentScaleX * scaleFactor,
          scaleY: currentScaleY * scaleFactor
        });
      }
    } else {
      // No question, scale with canvas
      drawing.set({
        left: currentLeft * scaleX,
        top: currentTop * scaleY,
        scaleX: currentScaleX * scaleFactor,
        scaleY: currentScaleY * scaleFactor
      });
    }
    
    drawing.setCoords();
  });

  // Handle optical choices - they should move with the question
  if (questionBg && opticalChoices.length > 0) {
    const scaleRatio = questionScale / ((questionBg as any)._previousScale || questionBg.scaleX || 1);
    
    opticalChoices.forEach(choice => {
      const currentLeft = choice.left || 0;
      const currentTop = choice.top || 0;
      
      choice.set({
        left: currentLeft + questionOffsetX,
        top: currentTop + questionOffsetY,
        scaleX: (choice.scaleX || 1) * scaleRatio,
        scaleY: (choice.scaleY || 1) * scaleRatio
      });
      choice.setCoords();
    });
    
    // Store current scale for next resize
    (questionBg as any)._previousScale = questionScale;
  }

  // Handle other question elements
  questionElements.forEach(elem => {
    if (elem === questionBg) return; // Already handled
    
    const name = getObjectName(elem);
    const currentLeft = elem.left || 0;
    const currentTop = elem.top || 0;
    
    if (name === 'parentQuestionBackground') {
      // Parent image scales and moves with main question
      elem.set({
        left: currentLeft + questionOffsetX,
        top: currentTop + questionOffsetY,
        scaleX: questionScale,
        scaleY: questionScale
      });
    } else {
      // Handles, frames, etc. move with question
      elem.set({
        left: currentLeft + questionOffsetX,
        top: currentTop + questionOffsetY
      });
    }
    elem.setCoords();
  });

  // Render all changes
  canvas.requestRenderAll();
};

// Helper to track canvas dimensions
export const trackCanvasDimensions = (canvas: CanvasType) => {
  return {
    width: canvas.getWidth(),
    height: canvas.getHeight()
  };
};